/**
 * Community entity editor (W.2) — declare a new community or edit an existing
 * one's identity + population figures. Closes the PRD §6 boundary ("no community
 * map editor for the community entity itself"). The matching map interactions
 * (place-a-point to declare, re-place to move the centroid) live in
 * CommunityPlaceTool; this is the form half.
 *
 *   mode 'declare'  — POST /api/communities (audited declare_community); the
 *                     centroid is the point the operator just placed (read-only
 *                     here — moving it is the separate "Move on map" flow).
 *   mode 'edit'     — PATCH /api/communities/:id (audited edit_community);
 *                     overwrites the approximate seeded figures with real ones.
 *
 * Modal pattern mirrors HeadcountEditorForm — Esc/backdrop close, validate before
 * submit, toast on success.
 */
import { useEffect, useState } from 'react';
import type { Community, CommunityType } from '@tsr/shared-types';
import { createCommunity, patchCommunity } from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';

const TYPE_OPTIONS: { value: CommunityType; label: string }[] = [
  { value: 'municipality', label: 'Municipality' },
  { value: 'first_nation', label: 'First Nation' },
  { value: 'metis_settlement', label: 'Métis Settlement' },
  { value: 'inuit_community', label: 'Inuit Community' },
  { value: 'unincorporated', label: 'Unincorporated' },
  { value: 'other', label: 'Other' },
];

interface DeclareProps {
  mode: 'declare';
  /** The centroid the operator just placed on the map. */
  coords: { lon: number; lat: number };
}
interface EditProps {
  mode: 'edit';
  /** The community being edited (prefills the form). */
  community: Pick<
    Community,
    'id' | 'name' | 'community_type' | 'jurisdiction' | 'population' | 'vulnerable_population'
  >;
}

type CommunityEntityFormProps = (DeclareProps | EditProps) & {
  onClose: () => void;
  onSubmitted: (community: Community) => void;
};

/** Parse an optional non-negative integer field. '' → null; bad → undefined. */
function parsePop(v: string): number | null | undefined {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

export function CommunityEntityForm(props: CommunityEntityFormProps): JSX.Element {
  const { mode, onClose, onSubmitted } = props;
  const existing = mode === 'edit' ? props.community : null;

  const [name, setName] = useState(existing?.name ?? '');
  const [type, setType] = useState<CommunityType>(existing?.community_type ?? 'municipality');
  const [jurisdiction, setJurisdiction] = useState(existing?.jurisdiction ?? '');
  const [population, setPopulation] = useState(
    existing?.population != null ? String(existing.population) : '',
  );
  const [vulnerable, setVulnerable] = useState(
    existing?.vulnerable_population != null ? String(existing.vulnerable_population) : '',
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = (): void => {
    if (busy) return;
    if (name.trim().length === 0) {
      showToast('Community name is required.', { variant: 'error' });
      return;
    }
    const pop = parsePop(population);
    const vuln = parsePop(vulnerable);
    if (pop === undefined || vuln === undefined) {
      showToast('Population figures must be non-negative whole numbers.', { variant: 'error' });
      return;
    }

    setBusy(true);
    const session_id = getSessionId();
    const work =
      mode === 'declare'
        ? createCommunity({
            name: name.trim(),
            community_type: type,
            jurisdiction: jurisdiction.trim() || undefined,
            lon: props.coords.lon,
            lat: props.coords.lat,
            population: pop,
            vulnerable_population: vuln,
            metadata: { source: 'operator_declared' },
            session_id,
          })
        : patchCommunity(props.community.id, {
            name: name.trim(),
            community_type: type,
            jurisdiction: jurisdiction.trim() || null,
            population: pop,
            vulnerable_population: vuln,
            session_id,
          });

    void work
      .then((community) => {
        showToast(mode === 'declare' ? 'Community added.' : 'Community updated.', {
          variant: 'success',
        });
        onSubmitted(community);
      })
      .catch(() =>
        showToast(
          mode === 'declare'
            ? 'Could not add the community. Please retry.'
            : 'Could not update the community. Please retry.',
          { variant: 'error' },
        ),
      )
      .finally(() => setBusy(false));
  };

  const inputCls =
    'w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100 placeholder-slate-500';

  return (
    <div
      data-testid="community-form-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
    >
      <div
        data-testid="community-form-modal"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-md max-h-[88vh] rounded-lg border border-sky-700/60 bg-slate-900 text-slate-100 shadow-2xl"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h2 className="text-sm font-semibold tracking-tight">
            {mode === 'declare' ? 'Add community' : 'Edit community'}
            {mode === 'edit' && <span className="text-sky-300"> — {existing?.name}</span>}
          </h2>
          <button
            type="button"
            data-testid="community-form-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {mode === 'declare' && (
            <p className="text-[11px] text-slate-400" data-testid="community-form-coords">
              Centroid{' '}
              <span className="font-mono text-slate-300">
                {props.coords.lat.toFixed(4)}, {props.coords.lon.toFixed(4)}
              </span>{' '}
              — placed on the map. Use “Move on map” later to re-place it.
            </p>
          )}

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Name *</span>
            <input
              data-testid="community-name"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kashechewan First Nation"
              className={inputCls}
            />
          </label>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Type</span>
            <select
              data-testid="community-type"
              value={type}
              onChange={(e) => setType(e.target.value as CommunityType)}
              className={inputCls}
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Jurisdiction (optional)</span>
            <input
              data-testid="community-jurisdiction"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              placeholder="e.g. Ontario — Kenora District"
              className={inputCls}
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-slate-400 space-y-1">
              <span>Population</span>
              <input
                type="number"
                min={0}
                step={1}
                data-testid="community-population"
                value={population}
                onChange={(e) => setPopulation(e.target.value)}
                placeholder="—"
                className={inputCls}
              />
            </label>
            <label className="text-[11px] text-slate-400 space-y-1">
              <span>Vulnerable</span>
              <input
                type="number"
                min={0}
                step={1}
                data-testid="community-vulnerable"
                value={vulnerable}
                onChange={(e) => setVulnerable(e.target.value)}
                placeholder="—"
                className={inputCls}
              />
            </label>
          </div>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            Overwrite the approximate seeded figures with real numbers when you have them.
          </p>
        </div>

        <footer className="flex items-center gap-2 px-5 py-3 border-t border-slate-700">
          <button
            type="button"
            data-testid="community-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs py-1.5 px-3 rounded bg-sky-600/90 hover:bg-sky-600 border border-sky-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy
              ? mode === 'declare'
                ? 'Adding…'
                : 'Saving…'
              : mode === 'declare'
                ? 'Add community'
                : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}
